import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Input,
  Button,
  message,
  Layout,
  Form,
  Divider,
  Space,
} from "antd";
import { SaveOutlined, LogoutOutlined } from "@ant-design/icons";
import { SessionContext } from "../providers/SessionProvider";

import "../BaseStyles.css";
import { AuthService } from "../services/AuthService";

const { Content } = Layout;

export const UserPage = () => {
  const api = AuthService;

  const [loading, setLoading] = useState(false);

  const session = useContext(SessionContext);
  const [user, setUser] = useState(session.user);

  const [form] = Form.useForm();

  const onFinish = (values: any) => {
    assign();
  };

  const fetch = useCallback(() => {
    setLoading(true);
    api
      .current()
      .then((user) => setUser(user))
      .catch((error) => message.error(error.message))
      .finally(() => setLoading(false));
  }, [api]);

  const assign = () => {
    setLoading(true);
    const { jiraApiToken, githubApiToken } = form.getFieldsValue();
    api
      .token({ jiraApiToken, githubApiToken })
      .then((user) => setUser(user))
      .catch((error) => message.error(error.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    form.setFieldsValue(user);
  }, [user, form]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const JiraApiTokenDisclaimer = () => {
    // Bullet list
    return (
      <>
        <ul>
          <li>
            Log in to{" "}
            <a
              href="https://id.atlassian.com/manage-profile/security/api-tokens"
              target="_blank"
              rel="noreferrer"
            >
              https://id.atlassian.com/manage-profile/security/api-tokens
            </a>
          </li>
          <li>
            Click <b>Create API token</b>.
          </li>
          <li>
            From the dialog that appears, enter a memorable and concise{" "}
            <b>Label</b>
            for your token and click Create.
          </li>
          <li>
            Click <b>Copy to clipboard</b>, then paste the token to this field.
          </li>
        </ul>
      </>
    );
  };

  const GitHubApiTokenDisclaimer = () => {
    // Bullet list
    return (
      <>
        <ul>
          <li>
            Generate Fine Grained token using{" "}
            <a
              href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens#creating-a-fine-grained-personal-access-token"
              target="_blank"
              rel="noreferrer"
            >
              this guide
            </a>
          </li>
          <li>
            Default permissions with read only access to public repos should be
            enough.
          </li>
          <li>
            Click <b>Copy to clipboard</b>, then paste the token to this field.
          </li>
        </ul>
      </>
    );
  };

  return (
    <Layout>
      <Content>
        <Form
          form={form}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
          layout="vertical"
          onFinish={onFinish}
        >
          <Divider orientation="left">Appodeal</Divider>
          <Form.Item label="User ID" name="id">
            <Input disabled />
          </Form.Item>
          <Form.Item label="User Name" name="name">
            <Input disabled />
          </Form.Item>
          <Form.Item label="User Email" name="email">
            <Input disabled />
          </Form.Item>
          <Divider orientation="left">Environment</Divider>
          <Form.Item label="API URL">
            <Input value={process.env.REACT_APP_API_NEW_URL} disabled />
          </Form.Item>
          <Divider orientation="left">Tokens</Divider>
          <Form.Item
            extra={<JiraApiTokenDisclaimer />}
            label="Jira API Token"
            name="jiraApiToken"
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            label="GitHub fine-grained personal access token"
            name="githubApiToken"
            extra={<GitHubApiTokenDisclaimer />}
          >
            <Input.Password />
          </Form.Item>
          <Divider orientation="left" />
          <Form.Item>
            <Space>
              <Button
                loading={loading}
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
              >
                Save
              </Button>
              <Button
                htmlType="button"
                danger
                onClick={() => session.signout()}
                icon={<LogoutOutlined />}
              >
                Logout
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Content>
    </Layout>
  );
};
